import { Box, ButtonBase, Typography } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useContext, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { AppContext } from "../../context/AppContext";
import { ChartEntry } from "../../interfaces/AppDefinitions";
import { formatChartData, formatCmpctNumber } from "../../helpers/utils";
import { INK, LINE, MUTED } from "./shared";

const MORTGAGE_COLOR = "#8b95a7";

const RANGES = [
	{ id: "6M", label: "6 months", months: 6 },
	{ id: "1Y", label: "1 year", months: 12 },
	{ id: "MAX", label: "All", months: 0 },
];

// Keeps the points from the last `months` months (all of them for 0). Falls back to every point
// when the window would leave fewer than two, so the chart never shows a single dot.
function pointsInRange(data: ChartEntry[], months: number) {
	if (!months) return data;
	const start = new Date();
	start.setMonth(start.getMonth() - months);
	start.setDate(1);
	start.setHours(0, 0, 0, 0);
	const inRange = data.filter((point) => new Date(point.x) >= start);
	return inRange.length >= 2 ? inRange : data;
}

// Mortgage points between the first and last value points, so the two lines line up.
function alignedMortgage(mortgage: ChartEntry[] | undefined, points: ChartEntry[]) {
	if (!mortgage?.length || points.length < 2) return [];
	const first = new Date(points[0].x).getTime();
	const last = new Date(points[points.length - 1].x).getTime();
	return mortgage.filter((p) => {
		const t = new Date(p.x).getTime();
		return t >= first && t <= last;
	});
}

export default function ValueChart(props: { data: ChartEntry[]; height: number; mortgage?: ChartEntry[] }) {
	const { appState } = useContext(AppContext);
	const { colorPallete, info } = appState;
	const [range, setRange] = useState("1Y");
	const isBar = info.agent.valuationChartType === "BAR";
	const points = useMemo(
		() => pointsInRange(props.data ?? [], RANGES.find((r) => r.id === range)?.months ?? 0),
		[props.data, range],
	);
	const mortgagePoints = useMemo(() => alignedMortgage(props.mortgage, points), [props.mortgage, points]);
	const withMortgage = mortgagePoints.length >= 2;
	const ys = points
		.concat(withMortgage ? mortgagePoints : [])
		.map((point) => Number(point.y))
		.filter((y) => isFinite(y));
	const minY = ys.length ? Math.min(...ys) * 0.97 : 0;
	const maxY = ys.length ? Math.max(...ys) * 1.02 : 0;

	const options: ApexOptions = {
		chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", parentHeightOffset: 0 },
		colors: [isBar ? colorPallete["scheme5"] : colorPallete["scheme4"], MORTGAGE_COLOR],
		fill: isBar
			? { type: "solid", opacity: withMortgage ? [0.85, 1] : 0.85 }
			: {
					type: withMortgage ? ["gradient", "solid"] : "gradient",
					colors: [colorPallete["scheme5"], MORTGAGE_COLOR],
					gradient: { shadeIntensity: 0, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] },
				},
		stroke: isBar
			? { width: withMortgage ? [0, 2] : 0, dashArray: withMortgage ? [0, 5] : 0 }
			: { curve: "smooth", width: withMortgage ? [2.5, 2] : 2.5, dashArray: withMortgage ? [0, 5] : 0 },
		plotOptions: { bar: { borderRadius: 3, columnWidth: "62%" } },
		markers: { size: 0, hover: { size: 5 } },
		dataLabels: { enabled: false },
		grid: {
			borderColor: LINE,
			strokeDashArray: 0,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
			padding: { left: 4, right: 8 },
		},
		xaxis: {
			type: "datetime",
			labels: { datetimeUTC: true, format: "MMM yy", style: { colors: MUTED, fontSize: "11px" } },
			axisBorder: { show: false },
			axisTicks: { show: false },
			tooltip: { enabled: false },
		},
		yaxis: {
			min: minY,
			max: maxY,
			tickAmount: 4,
			labels: {
				style: { colors: MUTED, fontSize: "11px" },
				formatter: (value: number) => formatChartData(value),
			},
		},
		tooltip: {
			x: { format: "MMMM yyyy" },
			y: { formatter: (value: number) => formatCmpctNumber(value) },
			marker: { show: false },
		},
		legend: {
			show: withMortgage,
			position: "top",
			horizontalAlign: "left",
			fontSize: "12px",
			labels: { colors: MUTED },
			markers: { size: 5 },
		},
	};
	const series = withMortgage
		? [
				{ name: "Estimated value", type: isBar ? "column" : "area", data: points },
				{ name: "Mortgage balance (estimated)", type: "line", data: mortgagePoints },
			]
		: [{ name: "Estimated value", data: points }];

	return (
		<Box>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "space-between",
					alignItems: "center",
					gap: "8px 12px",
					mb: "8px",
				}}
			>
				<Typography component="h2" sx={{ fontSize: "16px", fontWeight: 700, color: INK }}>
					Home value over time
				</Typography>
				<Box
					role="radiogroup"
					aria-label="Chart range"
					sx={{
						display: "inline-flex",
						p: "3px",
						gap: "2px",
						borderRadius: "9px",
						backgroundColor: "#f1f3f7",
					}}
				>
					{RANGES.map((r) => (
						<ButtonBase
							key={r.id}
							role="radio"
							aria-checked={range === r.id}
							onClick={() => setRange(r.id)}
							sx={{
								px: "10px",
								py: "4px",
								borderRadius: "6px",
								fontSize: "12px",
								fontWeight: 600,
								whiteSpace: "nowrap",
								color: range === r.id ? INK : MUTED,
								backgroundColor: range === r.id ? "#fff" : "transparent",
								boxShadow: range === r.id ? "0 1px 2px rgba(0,0,0,.12)" : "none",
							}}
						>
							{r.label}
						</ButtonBase>
					))}
				</Box>
			</Box>
			<ReactApexChart
				key={`${isBar ? "bar" : "area"}-${withMortgage ? "m" : ""}`}
				series={series}
				options={options}
				type={withMortgage ? "line" : isBar ? "bar" : "area"}
				height={props.height}
			/>
		</Box>
	);
}
